// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from '@insite/client-framework/Types/WidgetProps';
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import printStyles from '@mcmc/XDL/Utilities/print';
import { printIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import React from "react";
import { css } from "styled-components";

const enum fields {
    margin = "margin",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.margin]: string;
    }
}

export interface PrintPageWidgetStyles {
    wrapper?: InjectableCss;
}

export const printPageWidgetStyles: PrintPageWidgetStyles = {
    wrapper: {
        css: css`
            float: right;
            ${printStyles.hideOnPrint};
            ${({margin}: any) => margin && `margin: ${margin};`}
        `,
    },

};

const styles = printPageWidgetStyles;

const printLinkClickHandler = () => {
    window.print();
};

type Props = OwnProps & {
    fields: {
        [fields.margin]: string;
    };
};

const PrintPageWidget: React.FC<Props> = ({ fields: { margin } }) => {
    return (
        <StyledWrapper {...styles.wrapper} {...({margin})}>
            <Link
                {...printIconStyle}
                onClick={printLinkClickHandler}
                aria-label={translate(`Print Page`)}
            >
                <VisuallyHidden>{translate("Print")}</VisuallyHidden>
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: PrintPageWidget,
    definition: {
        displayName: "Print Page",
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.margin,
                displayName: "Margin",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip:
                    "The value should be a valid css margin, for example: 5px will add margin to all directions, 5px 0 will add margin to top and bottom.",
                validate: (value: string) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
        ],
    },
};

export default widgetModule;